import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
  CashSpecialIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { SupportStrokeIcon } from "../assets/icons";
import { authPagePath } from "../auth/resume";
import { useStore } from "../store/globalState";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRequestPayableGuard } from "../hooks/useRequestPayableGuard";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";

/**
 * Class A interstitial after "Later" on the fund-request intention.
 * Pay now ≡ Accept → auth resume=transfer. No Home CTA.
 */
export const IntentionLaterPage = memo(() => {
  useClaimLink();
  useRequestPayableGuard();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.errorView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    footerColor,
    iconColors,
    backgroundIcon,
  } = colorConfig;

  useEffect(() => {
    safeCapture(getEvents().INTENTION_LATER_VIEW);
  }, []);

  const handlePayNow = () => {
    safeCapture(getEvents().INTENTION_LATER_PAY_NOW_CLICK);
    redirectTo(authPagePath({ kind: "transfer" }, { fromClaim: true }));
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header flow-landing-page">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            color={title}
            onIconClick={() => openSupport()}
            icon={
              <SupportStrokeIcon
                size={35}
                colors={[backgroundIcon, backgroundIcon]}
                glyphColor={iconColors[0]}
              />
            }
          />
        </Layout.Header>
        <Layout.Content className="landing-section flow-hero-section flow-hero-section--with-header">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <CashSpecialIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("fundRequest.laterScreen.highlight")}
                highlightColor={title}
                secudnary={t("fundRequest.laterScreen.secondary")}
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>
                {t("fundRequest.laterScreen.body")}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <BodyCopy textColor={footerColor} className="footer-text">
              {t("fundRequest.laterScreen.changedMind")}
            </BodyCopy>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handlePayNow}
            >
              {t("fundRequest.laterScreen.cta")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
